/* =============================================================
   COMPANIES — DATA ACCESS
   =============================================================
   Every query names its columns. `select('*')` is avoided as a
   habit here, because on `berths` it is actively wrong (see
   berths.ts) and a consistent rule is easier to follow than an
   exception.
   ============================================================= */

import { supabase } from './supabase'
import { toDataError } from './errors'
import type { Company, CompanyInput, CompanyWithCounts } from './types'

const COLUMNS = 'id, name, country_code, vat_number, contact_email, website, created_at'

/**
 * Normalise the optional fields: an empty box means "not set", not "" — the
 * CHECK constraints reject a blank string precisely so that NULL is the only
 * way to say "no value".
 *
 * A website is given a scheme if it has none. Someone typing
 * `stadtwerke-passau.de` means a URL, but stored bare it would render as a
 * relative link and navigate to admin.riveratlas.eu/stadtwerke-passau.de. The
 * database rejects it either way; prepending here means the form accepts what
 * people actually type instead of teaching them the constraint.
 */
function clean(input: CompanyInput): CompanyInput {
  const vat = input.vat_number?.trim()
  const site = input.website?.trim()
  return {
    name: input.name.trim(),
    country_code: input.country_code,
    contact_email: input.contact_email.trim(),
    vat_number: vat ? vat : null,
    website: site ? (/^https?:\/\//i.test(site) ? site : `https://${site}`) : null,
  }
}

/*
 * `berths(count)` and `vessels(count)` are PostgREST embedded aggregates:
 * counted in the database, one request, no rows transferred. The response
 * shape is a one-element array per embed — `"berths": [{ "count": 3 }]` — so
 * `countOf` unwraps it.
 *
 * Both run under RLS, so they count what the CALLER may read. See the note on
 * CompanyWithCounts.
 */
const COUNT_COLUMNS = `${COLUMNS}, berths(count), vessels(count)`

type CountEmbed = { count: number }[]
type CountedRow = Company & { berths: CountEmbed; vessels: CountEmbed }

function countOf(embed: CountEmbed | null | undefined): number {
  return embed?.[0]?.count ?? 0
}

function withCounts(row: CountedRow): CompanyWithCounts {
  return { ...row, berths: countOf(row.berths), vessels: countOf(row.vessels) }
}

export async function listCompanies(): Promise<Company[]> {
  const { data, error } = await supabase
    .from('companies')
    .select(COLUMNS)
    .order('name', { ascending: true })

  if (error) throw toDataError(error, 'Could not load companies.')
  return data ?? []
}

/**
 * How many rows there are, without fetching any.
 *
 * `head: true` makes it a HEAD request and `count: 'exact'` puts the total in
 * the Content-Range header, so the dashboard costs a couple of response headers
 * rather than every company row it would immediately throw away. It runs under
 * the same select policy as everything else, so the number is what you may see.
 */
export async function countCompanies(): Promise<number> {
  const { count, error } = await supabase
    .from('companies')
    .select('id', { count: 'exact', head: true })

  if (error) throw toDataError(error, 'Could not count companies.')
  return count ?? 0
}

/**
 * The list page's read: every company with its berth and vessel counts.
 *
 * Separate from listCompanies() because the company pickers on the berth and
 * vessel forms have no use for the aggregates, and a dropdown should not pay
 * for two counts per row it will never render.
 */
export async function listCompaniesWithCounts(): Promise<CompanyWithCounts[]> {
  const { data, error } = await supabase
    .from('companies')
    .select(COUNT_COLUMNS)
    .order('name', { ascending: true })
    .overrideTypes<CountedRow[], { merge: false }>()

  if (error) throw toDataError(error, 'Could not load companies.')
  return (data ?? []).map(withCounts)
}

/** One company with its counts, for the detail page. Null when the id does not exist. */
export async function getCompanyWithCounts(id: number): Promise<CompanyWithCounts | null> {
  const { data, error } = await supabase
    .from('companies')
    .select(COUNT_COLUMNS)
    .eq('id', id)
    .maybeSingle()
    .overrideTypes<CountedRow, { merge: false }>()

  if (error) throw toDataError(error, 'Could not load the company.')
  return data ? withCounts(data) : null
}

/** One company, for the edit form. Null when the id does not exist. */
export async function getCompany(id: number): Promise<Company | null> {
  const { data, error } = await supabase.from('companies').select(COLUMNS).eq('id', id).maybeSingle()

  if (error) throw toDataError(error, 'Could not load the company.')
  return data
}

export async function createCompany(input: CompanyInput): Promise<Company> {
  const { data, error } = await supabase
    .from('companies')
    .insert(clean(input))
    .select(COLUMNS)
    .single()

  if (error) throw toDataError(error, 'Could not create the company.')
  return data
}

export async function updateCompany(id: number, input: CompanyInput): Promise<Company> {
  const { data, error } = await supabase
    .from('companies')
    .update(clean(input))
    .eq('id', id)
    .select(COLUMNS)
    .single()

  if (error) throw toDataError(error, 'Could not save the company.')
  return data
}

export async function deleteCompany(id: number): Promise<void> {
  const { error } = await supabase.from('companies').delete().eq('id', id)
  if (error) throw toDataError(error, 'Could not delete the company.')
}
